import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { useRole } from "./RoleContext";
import { useEntitlement } from "./EntitlementContext";
import { isCurrentPass } from "../utils/moduleExpiry";
import { authFetch } from "../utils/authFetch";
import { normalizeTrainingTopicId, type SecurityModuleMeta, type TrainingLevel } from "../data/securityModules";
import { filterActiveModules, getEffectiveModulesForUser, isModuleLevelVisibleForUser, normalizeModulePlanOverrides, readActiveModules } from "../utils/modulePlanConfig";
import { readTrainingPlans } from "../utils/trainingPlans";
import type { Loc } from "../utils/customModule";
import { identityEmail } from "../utils/memberSession";
import { isTrainingExempt } from "../utils/userLoginStatus";
import type { TrainingDutyFields } from "../utils/trainingDuty";

type CustomModuleLite = { id: string; shortTitle: Loc; topicId: string; level: TrainingLevel; icon: string; color: string };

type DirectReportUser = {
  id: string;
  email: string;
  display_name: string | null;
  department: string | null;
  shared_member?: boolean;
  never_logged_in?: boolean;
  /** Unmittelbarer Vorgesetzter (E-Mail) – bei indirekt Unterstellten nicht man selbst. */
  reports_to?: string;
  /** Ebene unter dem eingeloggten Manager: 1 = direkt, 2 = Mitarbeiter eines direkt Unterstellten, … */
  level?: number;
  /** Nur als Co-Manager: Adresse des Vorgesetzten, zu dessen Team die Person gehört. */
  team_of?: string;
};

type PersonRef = { email: string; display_name: string | null };

type UserProfile = {
  level?: TrainingLevel;
  department?: string;
  departments?: string[];
  managerEmail?: string;
  trainingExempt?: boolean;
  trainingExemptFrom?: string;
  trainingExemptUntil?: string;
};

type Completion = { userEmail: string; topicId: string; moduleId?: string; score: number; completedAt: string; validUntil?: string | null };

type LevelAssignment = { userId: string; userEmail?: string; levelId: TrainingLevel };

export type ManagedOpenModule =
  | { kind: "standard"; module: SecurityModuleMeta }
  | { kind: "custom"; id: string; icon: string; color: string; shortTitle: Loc; topicId: string };

/** Ein zugewiesenes Modul mit Nachweis: wann zuletzt bestanden (null = offen). */
export type ManagedModuleRecord = { module: ManagedOpenModule; completedAt: string | null; score: number | null };

export type ManagedEmployee = {
  id: string;
  email: string;
  displayName: string;
  department: string | null;
  assignedCount: number;
  openModules: ManagedOpenModule[];
  modules: ManagedModuleRecord[];
  exempt: boolean;
  /** Schulungspflicht-Felder aus dem Profil – für die einheitliche Anzeige samt Datum. */
  duty: TrainingDutyFields;
  sharedMember: boolean;
  /** Steht im Verzeichnis, hat AWARE aber noch nie geöffnet. */
  notLoggedIn: boolean;
  /** Nie angemeldet UND in keiner Gruppe: steht in der Liste, zählt aber nicht mit. */
  notCounted: boolean;
  /** 1 = direkt unterstellt, höher = über weitere Führungskräfte. */
  level: number;
  reportsToEmail: string | null;
  reportsToName: string | null;
  /** Nur als Co-Manager: wessen Team das ist (null = das eigene). */
  teamOfName: string | null;
};

export type CoManager = { email: string; displayName: string };
export type RemindAllResult = { ok: boolean; reminded: number; mailFailed: number };
export type CoManagerResult = { ok: boolean; error?: string; max?: number };

export type RemindResult = { ok: boolean; deliveredAs?: "mail" | "note"; mailSent?: boolean; mailReason?: string | null };

type ManagerContextValue = {
  isManager: boolean;
  isLoading: boolean;
  team: ManagedEmployee[];
  deadline: string | null;
  managerName: string;
  remind: (email: string, kind: "mail" | "note", text: string, toName?: string) => Promise<RemindResult>;
  /** `until` (YYYY-MM-DD, einschließlich) leer = unbefristet. */
  setExempt: (email: string, exempt: boolean, until?: string) => Promise<boolean>;
  /** Wunsch nach einer neuen Schulung – geht per Mail aus dem eigenen Postfach an SHERLOX. */
  wishTraining: (topic: string, text: string) => Promise<{ ok: boolean; mailSent: boolean }>;
  /** Dieselbe Erinnerung an mehrere Personen – ein Aufruf, ein Schreibvorgang. */
  remindAll: (targets: { email: string; toName: string }[], kind: "mail" | "note", text: string) => Promise<RemindAllResult>;
  /** Wen ich als Co-Manager eingetragen habe. */
  coManagers: CoManager[];
  /** Für wen ich Co-Manager bin (deren Team sehe ich mit). */
  coManagerFor: CoManager[];
  /** Nur wer ein eigenes Team führt, kann eine Vertretung eintragen. */
  canAppointCoManager: boolean;
  addCoManager: (email: string) => Promise<CoManagerResult>;
  removeCoManager: (email: string) => Promise<CoManagerResult>;
};

const ManagerContext = createContext<ManagerContextValue>({
  isManager: false,
  isLoading: true,
  team: [],
  deadline: null,
  managerName: "",
  remind: async () => ({ ok: false }),
  setExempt: async () => false,
  wishTraining: async () => ({ ok: false, mailSent: false }),
  remindAll: async () => ({ ok: false, reminded: 0, mailFailed: 0 }),
  coManagers: [],
  coManagerFor: [],
  canAppointCoManager: false,
  addCoManager: async () => ({ ok: false }),
  removeCoManager: async () => ({ ok: false }),
});

function toCoManagers(list: unknown): CoManager[] {
  if (!Array.isArray(list)) return [];
  return (list as PersonRef[])
    .filter(p => p && typeof p.email === "string")
    .map(p => ({ email: p.email, displayName: p.display_name ?? p.email }));
}

function normalizeList(values: (string | undefined | null)[]): string[] {
  return Array.from(new Set(values.map(v => (v ?? "").trim()).filter(Boolean)));
}

function completionMatchesModule(completion: Completion, module: SecurityModuleMeta): boolean {
  const completionTopicId = normalizeTrainingTopicId(completion.moduleId ?? completion.topicId) ?? normalizeTrainingTopicId(completion.topicId) ?? completion.topicId;
  const moduleTopicId = normalizeTrainingTopicId(module.topicId) ?? module.topicId;
  return completion.moduleId === module.id || completionTopicId === moduleTopicId || completionTopicId === module.id;
}

export function ManagerProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const { tenantId } = useRole();
  const { entitlement } = useEntitlement();
  const account = instance.getActiveAccount() ?? accounts[0];
  const myEmail = identityEmail(account?.username);

  const [directReports, setDirectReports] = useState<DirectReportUser[] | null>(null);
  const [profiles, setProfiles] = useState<Record<string, UserProfile>>({});
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [levelAssignments, setLevelAssignments] = useState<LevelAssignment[]>([]);
  const [customModules, setCustomModules] = useState<CustomModuleLite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deadline, setDeadlineState] = useState<string | null>(null);
  const [coManagers, setCoManagers] = useState<CoManager[]>([]);
  const [coManagerFor, setCoManagerFor] = useState<CoManager[]>([]);
  const [canAppointCoManager, setCanAppointCoManager] = useState(false);
  const managerName = account?.name ?? myEmail;

  useEffect(() => {
    // Tenant-weite Schulungs-Frist wird vom Admin gesetzt (features.training_deadline).
    const features = entitlement?.features as { training_deadline?: { date?: string } | null } | undefined;
    setDeadlineState(features?.training_deadline?.date ?? null);
  }, [entitlement]);

  const postActionRaw = useCallback(async (payload: Record<string, unknown>): Promise<{ ok: boolean; data: Record<string, unknown> }> => {
    if (!tenantId) return { ok: false, data: {} };
    try {
      const r = await authFetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, managerAction: payload }),
      });
      const data = await r.json().catch(() => ({} as Record<string, unknown>));
      return { ok: r.ok, data: (data && typeof data === "object" ? data : {}) as Record<string, unknown> };
    } catch {
      return { ok: false, data: {} };
    }
  }, [tenantId]);

  const postAction = useCallback(async (payload: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
    const res = await postActionRaw(payload);
    return res.ok ? res.data : null;
  }, [postActionRaw]);

  const remind = useCallback(async (email: string, kind: "mail" | "note", text: string, toName?: string): Promise<RemindResult> => {
    const data = await postAction({ action: "reminder", email: email.toLowerCase(), kind, text, fromName: managerName, toName });
    if (!data) return { ok: false };
    return {
      ok: true,
      deliveredAs: data.deliveredAs === "mail" ? "mail" : "note",
      mailSent: data.mailSent as boolean | undefined,
      mailReason: (data.mailReason as string | null) ?? null,
    };
  }, [postAction, managerName]);

  const setExemptAction = useCallback(async (email: string, exempt: boolean, until: string = ""): Promise<boolean> => {
    const data = await postAction({ action: "exempt", email: email.toLowerCase(), exempt, until: exempt ? until : "" });
    if (!data) return false;
    const key = email.toLowerCase();
    // Der Server antwortet mit dem gespeicherten Stand – den übernehmen, nicht den gewünschten.
    const savedUntil = typeof data.until === "string" && data.until ? data.until : undefined;
    setProfiles(prev => ({ ...prev, [key]: { ...(prev[key] ?? {}), trainingExempt: data.exempt === true, trainingExemptFrom: undefined, trainingExemptUntil: savedUntil } }));
    return true;
  }, [postAction]);

  const wishTraining = useCallback(async (topic: string, text: string) => {
    const data = await postAction({ action: "trainingWish", topic, text, fromName: managerName });
    return { ok: Boolean(data), mailSent: data?.mailSent === true };
  }, [postAction, managerName]);

  const remindAll = useCallback(async (targets: { email: string; toName: string }[], kind: "mail" | "note", text: string): Promise<RemindAllResult> => {
    const data = await postAction({ action: "reminderBulk", kind, text, fromName: managerName, targets: targets.map(t => ({ email: t.email.toLowerCase(), toName: t.toName })) });
    const results = data && Array.isArray(data.results) ? (data.results as { deliveredAs?: string; mailSent?: boolean }[]) : null;
    if (!results) return { ok: false, reminded: 0, mailFailed: 0 };
    return { ok: true, reminded: results.length, mailFailed: results.filter(r => r.deliveredAs === "mail" && r.mailSent !== true).length };
  }, [postAction, managerName]);

  const changeCoManager = useCallback(async (action: "coManagerAdd" | "coManagerRemove", email: string): Promise<CoManagerResult> => {
    const res = await postActionRaw({ action, email: email.toLowerCase().trim() });
    if (!res.ok) {
      return { ok: false, error: typeof res.data.error === "string" ? res.data.error : undefined, max: typeof res.data.max === "number" ? res.data.max : undefined };
    }
    // Der Server antwortet mit dem gespeicherten Stand – den übernehmen.
    setCoManagers(toCoManagers(res.data.coManagers));
    return { ok: true };
  }, [postActionRaw]);
  const addCoManager = useCallback((email: string) => changeCoManager("coManagerAdd", email), [changeCoManager]);
  const removeCoManager = useCallback((email: string) => changeCoManager("coManagerRemove", email), [changeCoManager]);

  useEffect(() => {
    if (!tenantId) return;
    authFetch(`/api/phishing-campaigns?tenantId=${encodeURIComponent(tenantId)}&customModules=1`)
      .then(r => r.ok ? r.json() : { customModules: [] })
      .then((data: { customModules?: CustomModuleLite[] }) => setCustomModules(data.customModules ?? []))
      .catch(() => setCustomModules([]));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !myEmail) {
      setDirectReports([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    authFetch(`/api/users?tenantId=${encodeURIComponent(tenantId)}&myTeam=1`)
      .then(r => (r.ok ? r.json() : { directReports: [] }))
      .then((data: { directReports?: DirectReportUser[]; coManagers?: PersonRef[]; coManagerFor?: PersonRef[]; canAppointCoManager?: boolean }) => {
        if (cancelled) return;
        const reports = Array.isArray(data.directReports) ? data.directReports : [];
        setDirectReports(reports);
        setCoManagers(toCoManagers(data.coManagers));
        setCoManagerFor(toCoManagers(data.coManagerFor));
        setCanAppointCoManager(data.canAppointCoManager === true);
        if (reports.length === 0) {
          setIsLoading(false);
          return;
        }
        Promise.all([
          authFetch(`/api/user-profiles?tenantId=${encodeURIComponent(tenantId)}`).then(r => (r.ok ? r.json() : { userProfiles: {} })),
          authFetch(`/api/completions?tenantId=${encodeURIComponent(tenantId)}`).then(r => (r.ok ? r.json() : [])),
          authFetch(`/api/user-level-assignments?tenantId=${encodeURIComponent(tenantId)}`).then(r => (r.ok ? r.json() : [])),
        ])
          .then(([profData, compData, levelData]: [{ userProfiles?: Record<string, UserProfile> }, Completion[], LevelAssignment[]]) => {
            if (cancelled) return;
            setProfiles(profData?.userProfiles ?? {});
            setCompletions(Array.isArray(compData) ? compData : []);
            setLevelAssignments(Array.isArray(levelData) ? levelData : []);
          })
          .finally(() => {
            if (!cancelled) setIsLoading(false);
          });
      })
      .catch(() => {
        if (!cancelled) {
          setDirectReports([]);
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, myEmail]);

  const team = useMemo<ManagedEmployee[]>(() => {
    if (!directReports || directReports.length === 0) return [];
    const features = entitlement?.features;
    const groupModules = features?.group_modules ?? {};
    const overrides = normalizeModulePlanOverrides(features?.module_plan_overrides);
    const activeModules = readActiveModules(features ?? null);
    const defaultLevel: TrainingLevel = features?.default_level ?? "standard";
    const plans = readTrainingPlans(features ?? null, "de");

    return directReports.map(u => {
      const emailKey = u.email.toLowerCase();
      const profile = profiles[emailKey];
      const assignment = levelAssignments.find(a => a.userId === u.id || a.userEmail?.toLowerCase() === emailKey);
      const level = assignment?.levelId ?? profile?.level ?? defaultLevel;
      const userGroups = normalizeList([
        ...(Array.isArray(profile?.departments) ? profile.departments : []),
        profile?.department ?? "",
        u.department ?? "",
      ]);
      const assigned = filterActiveModules(
        getEffectiveModulesForUser(level, userGroups, groupModules, overrides, plans),
        activeModules,
      );
      const visibleCustomModules = filterActiveModules(
        customModules.filter(cm => isModuleLevelVisibleForUser(level, cm.level, plans)),
        activeModules,
      );
      const userCompletions = completions.filter(c => c.userEmail.toLowerCase() === emailKey);
      // Abgelaufene Abschlüsse gelten als offen – wie im Dashboard und in der Monatsmail.
      const passed = userCompletions.filter(c => isCurrentPass(c));
      // Nachweis = jüngster bestandener Abschluss (bei Wiederholungen zählt der aktuelle).
      const latest = (matches: Completion[]): Completion | null => matches.reduce<Completion | null>(
        (best, c) => (!best || String(c.completedAt) > String(best.completedAt) ? c : best),
        null,
      );
      const modules: ManagedModuleRecord[] = [
        ...assigned.map(module => {
          const hit = latest(passed.filter(c => completionMatchesModule(c, module)));
          return { module: { kind: "standard" as const, module }, completedAt: hit?.completedAt || null, score: hit ? hit.score : null };
        }),
        ...visibleCustomModules.map(cm => {
          const hit = latest(passed.filter(c => c.topicId === cm.topicId));
          return {
            module: { kind: "custom" as const, id: cm.id, icon: cm.icon, color: cm.color, shortTitle: cm.shortTitle, topicId: cm.topicId },
            completedAt: hit?.completedAt || null,
            score: hit ? hit.score : null,
          };
        }),
      ];
      const openModules: ManagedOpenModule[] = modules
        .filter(m => !passed.some(c => (m.module.kind === "standard" ? completionMatchesModule(c, m.module.module) : c.topicId === m.module.topicId)))
        .map(m => m.module);
      const reportsTo = (u.reports_to ?? "").toLowerCase();
      const teamOf = (u.team_of ?? "").toLowerCase();
      return {
        id: u.id,
        email: u.email,
        displayName: u.display_name ?? u.email,
        department: u.department,
        assignedCount: assigned.length + visibleCustomModules.length,
        openModules,
        modules,
        exempt: isTrainingExempt(profile),
        duty: { trainingExempt: profile?.trainingExempt, trainingExemptFrom: profile?.trainingExemptFrom, trainingExemptUntil: profile?.trainingExemptUntil },
        sharedMember: u.shared_member === true,
        notLoggedIn: u.never_logged_in === true,
        notCounted: u.never_logged_in === true && userGroups.length === 0,
        level: typeof u.level === "number" && u.level > 0 ? u.level : 1,
        reportsToEmail: reportsTo || null,
        reportsToName: reportsTo ? (directReports.find(r => r.email.toLowerCase() === reportsTo)?.display_name ?? coManagerFor.find(c => c.email.toLowerCase() === reportsTo)?.displayName ?? reportsTo) : null,
        teamOfName: teamOf ? (coManagerFor.find(c => c.email.toLowerCase() === teamOf)?.displayName ?? teamOf) : null,
      };
    });
  }, [directReports, profiles, completions, levelAssignments, customModules, entitlement, coManagerFor]);

  const value = useMemo<ManagerContextValue>(() => ({
    isManager: (directReports?.length ?? 0) > 0,
    isLoading,
    team,
    deadline,
    managerName,
    remind,
    setExempt: setExemptAction,
    wishTraining,
    remindAll,
    coManagers,
    coManagerFor,
    canAppointCoManager,
    addCoManager,
    removeCoManager,
  }), [directReports, isLoading, team, deadline, managerName, remind, setExemptAction, wishTraining, remindAll, coManagers, coManagerFor, canAppointCoManager, addCoManager, removeCoManager]);

  return <ManagerContext.Provider value={value}>{children}</ManagerContext.Provider>;
}

export function useManager() {
  return useContext(ManagerContext);
}
